/**
 * Peticiones al agente local vía fetch nativo con targetAddressSpace=loopback.
 * Chrome 130+ (Local Network Access) bloquea HttpClient/XHR desde HTTPS público
 * hacia localhost sin esta anotación y sin Permissions-Policy en el documento.
 */

export type LocalAgentFetchInit = Omit<RequestInit, 'credentials'> & {
  timeoutMs?: number;
};

type FetchWithAddressSpace = RequestInit & {
  targetAddressSpace?: 'loopback' | 'local' | 'public';
};

let permissionPreflightDone = false;

/** Solicita permiso LNA una vez por sesión (Chrome 142+). No falla si no está soportado. */
export async function ensureLocalNetworkAccessPermission(): Promise<void> {
  if (permissionPreflightDone) return;
  permissionPreflightDone = true;
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) return;
  try {
    await navigator.permissions.query({ name: 'local-network-access' as PermissionName });
  } catch {
    // API no disponible en este navegador/versión
  }
}

export async function localAgentFetch(url: string, init: LocalAgentFetchInit = {}): Promise<Response> {
  await ensureLocalNetworkAccessPermission();

  const { timeoutMs, signal, ...rest } = init;
  const controller = new AbortController();
  const timeoutId = timeoutMs != null && timeoutMs > 0
    ? setTimeout(() => controller.abort(), timeoutMs)
    : null;

  if (signal) {
    signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  const requestInit: FetchWithAddressSpace = {
    credentials: 'include',
    ...rest,
    signal: controller.signal,
    targetAddressSpace: 'loopback',
  };

  try {
    return await fetch(url, requestInit);
  } finally {
    if (timeoutId != null) clearTimeout(timeoutId);
  }
}
